import { useEffect, useState } from "react";
import { api, API_URL } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Download, TrendingUp, Users, Globe, MonitorSmartphone, Target, Timer, AlertTriangle } from "lucide-react";

/**
 * Batch 21 — Executive Donor Intelligence Platform.
 *
 * Admin-only. Extends the existing Donor Analytics view with attribution
 * (UTM, origin pages, referrers), audience insights (browser/OS/timezone),
 * timing metrics, and PDF/Excel/CSV exports. Public design, forms, donation
 * workflow and existing analytics are NOT touched.
 */
export default function AdminIntelligence() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState("");

    async function load() {
        setLoading(true);
        try {
            const res = await api.get(`/admin/analytics/executive?days=${days}`);
            setData(res.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Failed to load intelligence");
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

    async function download(fmt) {
        setDownloading(fmt);
        try {
            const url = `${API_URL}/admin/analytics/export.${fmt}?days=${days}`;
            const resp = await fetch(url, { credentials: "include" });
            if (!resp.ok) throw new Error("Export failed");
            const blob = await resp.blob();
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objectUrl;
            a.download = `donor-intelligence-${days}d.${fmt}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(objectUrl);
        } catch (e) {
            toast.error(e?.message || "Export failed");
        } finally { setDownloading(""); }
    }

    const k = data?.kpi || {};

    return (
        <div data-testid="admin-intelligence">
            <div className="flex flex-wrap justify-between items-end gap-3">
                <div>
                    <div className="gsf-eyebrow">Executive Donor Intelligence</div>
                    <h1 className="mt-2 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold" data-testid="intel-title">Executive Intelligence</h1>
                    {data && <p className="text-xs text-gray-500 mt-1" data-testid="intel-generated">Last {data.range_days} days · Generated {data.generated_at?.slice(0, 19).replace("T", " ")} UTC</p>}
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white" data-testid="intel-range-select">
                        <option value={7}>Last 7 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={90}>Last 90 days</option>
                        <option value={180}>Last 6 months</option>
                        <option value={365}>Last year</option>
                    </select>
                    <button onClick={load} className="gsf-btn-outline text-sm" data-testid="intel-refresh"><RefreshCw size={14} /> Refresh</button>
                    <button onClick={() => download("pdf")} className="gsf-btn-outline text-sm" data-testid="export-pdf" disabled={downloading === "pdf"}>
                        <Download size={14} /> {downloading === "pdf" ? "Preparing..." : "PDF"}
                    </button>
                    <button onClick={() => download("xlsx")} className="gsf-btn-outline text-sm" data-testid="export-xlsx" disabled={downloading === "xlsx"}>
                        <Download size={14} /> {downloading === "xlsx" ? "Preparing..." : "Excel"}
                    </button>
                    <button onClick={() => download("csv")} className="gsf-btn-outline text-sm" data-testid="export-csv" disabled={downloading === "csv"}>
                        <Download size={14} /> {downloading === "csv" ? "Preparing..." : "CSV"}
                    </button>
                </div>
            </div>

            {loading && <p className="mt-8 text-gray-500 text-sm" data-testid="intel-loading">Loading executive dataset…</p>}

            {data && (
                <>
                    <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="kpi-grid">
                        <Kpi icon={Users} label="Unique sessions" value={k.unique_sessions} testid="kpi-sessions" />
                        <Kpi icon={Target} label="Confirmations" value={k.confirmations} testid="kpi-confirmations" />
                        <Kpi icon={TrendingUp} label="Conversion" value={`${k.conversion_rate_pct}%`} testid="kpi-conversion" />
                        <Kpi icon={Timer} label="Median time to confirm" value={`${k.median_time_to_confirm_s}s`} testid="kpi-time" />
                        <Kpi icon={AlertTriangle} label="Avg. donation (USD)" value={`US$${k.avg_amount_usd || 0}`} testid="kpi-avg-amount" />
                    </div>

                    <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="gsf-card p-6 lg:col-span-3" data-testid="intel-funnel">
                            <h2 className="font-serif text-lg text-[color:var(--gsf-green-700)] font-semibold">Funnel</h2>
                            <FunnelBars funnel={data.funnel} />
                        </div>
                        <CountCard title="Campaigns (UTM)" items={data.campaigns} testid="intel-campaigns" icon={Target} />
                        <CountCard title="Sources" items={data.sources} testid="intel-sources" icon={Target} />
                        <CountCard title="Mediums" items={data.mediums} testid="intel-mediums" icon={Target} />
                        <CountCard title="Origin pages" items={data.origin_pages} testid="intel-origin-pages" icon={Target} />
                        <CountCard title="Referrers" items={data.referrers} testid="intel-referrers" icon={Target} />
                        <CountCard title="Programme interest" items={data.programmes} testid="intel-programmes" icon={Target} />
                        <CountCard title="Devices" items={data.devices} testid="intel-devices" icon={MonitorSmartphone} />
                        <CountCard title="Browsers" items={data.browsers} testid="intel-browsers" icon={MonitorSmartphone} />
                        <CountCard title="Operating systems" items={data.os} testid="intel-os" icon={MonitorSmartphone} />
                        <CountCard title="Countries" items={data.countries} testid="intel-countries" icon={Globe} />
                        <CountCard title="Timezones" items={data.timezones} testid="intel-timezones" icon={Globe} />
                        <CountCard title="Popular amounts (USD)" items={data.amounts} testid="intel-amounts" icon={TrendingUp}
                            formatKey={(x) => `US$${Number(x).toLocaleString()}`} />
                        <CountCard title="Payment methods" items={data.methods} testid="intel-methods" icon={TrendingUp} />
                        <CountCard title="Frequency" items={data.frequencies} testid="intel-frequencies" icon={TrendingUp} />
                        <CountCard title="Errors" items={data.errors} testid="intel-errors" icon={AlertTriangle} />

                        <div className="gsf-card p-6 lg:col-span-3" data-testid="intel-per-day">
                            <h2 className="font-serif text-lg text-[color:var(--gsf-green-700)] font-semibold">Events per day</h2>
                            <div className="mt-4 flex items-end gap-1 h-32">
                                {(data.per_day || []).map((d) => {
                                    const max = Math.max(...(data.per_day || []).map((x) => x.n), 1);
                                    const h = Math.max(2, Math.round((d.n / max) * 100));
                                    return <div key={d.date} title={`${d.date}: ${d.n}`} className="flex-1 bg-[color:var(--gsf-green-700)]/75 rounded-t" style={{ height: `${h}%` }} />;
                                })}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function Kpi({ icon: Icon, label, value, testid }) {
    return (
        <div className="gsf-card p-4 flex items-start gap-3" data-testid={testid}>
            <div className="w-9 h-9 rounded-lg bg-[color:var(--gsf-soft-green)] flex items-center justify-center text-[color:var(--gsf-green-700)]">
                <Icon size={16} strokeWidth={1.8} />
            </div>
            <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
                <div className="mt-1 font-serif text-xl text-[color:var(--gsf-green-700)] font-bold truncate">{value ?? "—"}</div>
            </div>
        </div>
    );
}

function FunnelBars({ funnel = {} }) {
    const stages = [
        ["donate_page_view", "Donate page"],
        ["amount_selected", "Amount"],
        ["frequency_selected", "Frequency"],
        ["payment_method_selected", "Payment method"],
        ["confirmation_submitted", "Confirmation"],
    ];
    const top = funnel.donate_page_view || 0;
    return (
        <div className="mt-4 space-y-2">
            {stages.map(([k, label]) => {
                const n = funnel[k] || 0;
                const pct = top > 0 ? Math.round((n / top) * 100) : 0;
                return (
                    <div key={k} data-testid={`funnel-${k}`}>
                        <div className="flex justify-between text-xs text-gray-600">
                            <span>{label}</span>
                            <span>{n.toLocaleString()} · {pct}%</span>
                        </div>
                        <div className="h-3 bg-gray-100 rounded overflow-hidden">
                            <div className="h-3 bg-[color:var(--gsf-green-700)]" style={{ width: `${pct}%` }} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function CountCard({ title, items, testid, formatKey, icon: Icon }) {
    const entries = Object.entries(items || {});
    const total = entries.reduce((s, [, n]) => s + n, 0);
    return (
        <div className="gsf-card p-6" data-testid={testid}>
            <div className="flex items-center gap-2">
                {Icon && <Icon size={16} className="text-[color:var(--gsf-green-700)]" strokeWidth={1.8} />}
                <h2 className="font-serif text-lg text-[color:var(--gsf-green-700)] font-semibold">{title}</h2>
            </div>
            {entries.length === 0 ? <p className="mt-2 text-xs text-gray-500">No data yet.</p> : (
                <ul className="mt-3 space-y-1.5 text-sm max-h-64 overflow-auto pr-1">
                    {entries.map(([k, n]) => {
                        const pct = total > 0 ? Math.round((n / total) * 100) : 0;
                        return (
                            <li key={k} className="flex justify-between gap-2">
                                <span className="truncate" title={k}>{formatKey ? formatKey(k) : k}</span>
                                <span className="text-gray-500 whitespace-nowrap">{n.toLocaleString()} · {pct}%</span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
